/**
 * referenceDialectSnapshot — the shared snapshot builder for tree/reference
 * dialects (Notion, Linear, and any future issue-tracker / doc-database).
 *
 * That Notion and Linear share ONE builder is the generalization proof made
 * concrete: a reference dialect is "objects with typed cross-references, an
 * optional parent, and an optional category color." The only per-dialect
 * inputs are the default relationship map, the containment cue value, and the
 * unnamed-relation fallback — everything else is identical. The canvas dialect
 * (Miro) differs only in carrying spatial layout, so it keeps its own builder.
 */

import {
  resolveRelationshipCue,
  type MiroColorRule,
  type RelationshipCueRule,
  type TypeMapRule,
} from '../fieldMappingRegistry';
import { resolveSignifierPatch } from '../whiteboardSignifiers';
import { DEFAULT_COLOR_TO_CATEGORY } from '../whiteboardImportService';
import type { ItemLanguageType } from '../../types/prioritization/lifecycle';
import type { RelationshipDirectionality } from '../../types/prioritization/itemRelationships';
import type { SemanticObject, SemanticRelation, SemanticSnapshot } from './snapshot';
import { emptySnapshot } from './snapshot';

/** A typed cross-reference: the cue (property/relation name) + the pages/issues it points at. */
export interface ReferenceLink {
  /** The relation NAME/TYPE — the cue resolved to a canonical type ('link_type'). */
  cue: string;
  targetExternalIds: string[];
}

export interface ReferenceObject {
  externalId: string;
  externalUrl?: string;
  title: string;
  description?: string;
  /** A dialect color (Notion option color, Linear label/state color). */
  categoryColor?: string;
  /** The parent object's external id when this is a sub-page / sub-issue. */
  parentExternalId?: string;
  relations: ReferenceLink[];
  /**
   * The object's own fields as the connector's field mappings name them
   * (a Linear issue's priority, state, labels, identifier…). Carried as
   * explicit assertions so they are recorded at rest and can be bound.
   */
  attributes?: Record<string, unknown>;
}

export interface ReferenceDialectRead {
  connectorType: string;
  container: { id: string; title: string; url?: string };
  objects: ReferenceObject[];
}

export interface ReferenceDialectRuleSet {
  /** Profile-shipped defaults (workspace v3 rules are prepended by the caller). */
  relationshipMap: RelationshipCueRule[];
  /** The cue value used for parent → contains (e.g. 'page' or 'parent'). */
  containmentCueValue: string;
  /** Fallback for a relation whose cue matches nothing — a proposal, never a drop. */
  unnamedRelation: { canonicalType: string; subtype?: string; directionality: 'directed' | 'undirected'; defaultConfidence: number; basis: SemanticRelation['assertion']['basis'] };
}

export interface ReferenceDialectMaps {
  relationshipMap?: RelationshipCueRule[];
  typeMap?: TypeMapRule[];
  colorRules?: MiroColorRule[];
}

function normalizeColor(color: string | undefined): string | undefined {
  if (!color) return undefined;
  const c = color.toLowerCase().trim();
  if (c === 'default' || c === '') return undefined;
  if (c === 'violet') return 'purple';
  // Linear (and any dialect) may give a hex color; the category vocabulary is
  // keyed by names, so bucket the hex to the nearest basic hue. Without this,
  // hex-color dialects would silently never map a category.
  if (/^#?[0-9a-f]{6}$/.test(c)) return hexToColorName(c);
  return c;
}

/** Bucket a 6-digit hex to a basic color name by hue/lightness. */
function hexToColorName(hex: string): string | undefined {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const light = (max + min) / 2 / 255;
  if (max - min < 24) {
    // Near-greyscale — no category signal (matches "unknown"/default behavior).
    return light > 0.85 ? undefined : 'gray';
  }
  let hue: number;
  const d = max - min;
  if (max === r) hue = ((g - b) / d) % 6;
  else if (max === g) hue = (b - r) / d + 2;
  else hue = (r - g) / d + 4;
  hue = (hue * 60 + 360) % 360;
  if (hue < 15 || hue >= 345) return 'red';
  if (hue < 45) return 'orange';
  if (hue < 70) return 'yellow';
  if (hue < 170) return 'green';
  if (hue < 200) return 'cyan';
  if (hue < 255) return 'blue';
  if (hue < 290) return 'purple';
  return 'pink';
}

function buildRelation(
  sourceExternalId: string,
  targetExternalId: string,
  rule: { canonicalType: string; subtype?: string; directionality?: 'directed' | 'undirected'; defaultConfidence: number; basis: SemanticRelation['assertion']['basis'] },
  cue: string,
): SemanticRelation {
  return {
    sourceExternalId,
    targetExternalId,
    canonicalType: rule.canonicalType as SemanticRelation['canonicalType'],
    ...(rule.subtype ? { subtype: rule.subtype } : {}),
    directionality: (rule.directionality ?? 'directed') as RelationshipDirectionality,
    cue,
    assertion: {
      value: rule.canonicalType as SemanticRelation['canonicalType'],
      confidence: rule.defaultConfidence,
      basis: rule.basis,
    },
  };
}

export function buildReferenceSnapshot(
  read: ReferenceDialectRead,
  maps: ReferenceDialectMaps,
  rules: ReferenceDialectRuleSet,
  options: { capturedAt?: string } = {},
): SemanticSnapshot {
  const capturedAt = options.capturedAt ?? new Date().toISOString();
  const snapshot = emptySnapshot({
    connectorType: read.connectorType,
    containerId: read.container.id,
    containerUrl: read.container.url,
    capturedAt,
  });

  const relationshipRules = [...(maps.relationshipMap ?? []), ...rules.relationshipMap];
  const colorRules = maps.colorRules ?? [];

  // The container (database / project) is the structural region.
  snapshot.regions.push({ externalId: read.container.id, title: read.container.title, kind: 'section' });

  // Objects → items. Tree dialects have no coordinates; external ids are stable
  // so proximity matching is never needed (layout stays undefined).
  for (const object of read.objects) {
    const color = normalizeColor(object.categoryColor);
    const semanticObject: SemanticObject = {
      externalId: object.externalId,
      nativeType: 'object',
      content: { text: object.title, ...(object.description ? { description: object.description } : {}) },
      visual: { ...(color ? { fill_color: color } : {}) },
      asserted: {},
    };
    const fieldEntries = Object.entries(object.attributes ?? {}).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    );
    if (fieldEntries.length > 0) {
      semanticObject.asserted.attributes = Object.fromEntries(
        fieldEntries.map(([key, value]) => [key, { value, confidence: 1, basis: 'explicit' as const }]),
      );
    }
    if (color) {
      const patch = resolveSignifierPatch({ fillColor: color }, colorRules);
      const ruleCategory = patch.itemCategory as ItemLanguageType | undefined;
      const defaultCategory = DEFAULT_COLOR_TO_CATEGORY[color] as ItemLanguageType | undefined;
      if (ruleCategory) {
        semanticObject.asserted.itemCategory = { value: ruleCategory, confidence: 0.9, basis: 'visual_language' };
      } else if (defaultCategory) {
        semanticObject.asserted.itemCategory = { value: defaultCategory, confidence: 0.75, basis: 'visual_language' };
      }
    }
    snapshot.objects.push(semanticObject);
  }

  const objectIds = new Set(snapshot.objects.map(o => o.externalId));

  // Typed cross-references → canonical edges. The cue NAME resolves the type;
  // an unmatched cue becomes a `related` proposal, never a silent drop.
  for (const object of read.objects) {
    for (const relation of object.relations) {
      const rule = resolveRelationshipCue([{ kind: 'link_type', value: relation.cue }], relationshipRules);
      for (const targetId of relation.targetExternalIds) {
        if (!objectIds.has(targetId) || targetId === object.externalId) {
          snapshot.unrecognized.push({ kind: 'relation', from: object.externalId, cue: relation.cue, to: targetId });
          continue;
        }
        if (rule) {
          snapshot.relations.push(buildRelation(object.externalId, targetId, rule, `relation “${relation.cue}”`));
        } else {
          snapshot.relations.push(buildRelation(object.externalId, targetId, rules.unnamedRelation, `unmapped relation “${relation.cue}”`));
        }
      }
    }
  }

  // Parent tree → containment.
  const containmentRule = resolveRelationshipCue([{ kind: 'containment', value: rules.containmentCueValue }], relationshipRules);
  if (containmentRule) {
    for (const object of read.objects) {
      if (!object.parentExternalId || !objectIds.has(object.parentExternalId) || object.parentExternalId === object.externalId) continue;
      snapshot.relations.push(buildRelation(object.parentExternalId, object.externalId, containmentRule, 'parent tree'));
    }
  }

  return snapshot;
}
